// Guest queue Inc 4a — the minimal bar read + Realtime. Mirrors useRoomAgenda:
// TanStack Query for the get_room_queue payload + a Supabase Realtime
// subscription (postgres_changes on room_grid_slots, filtered by room_id) that
// invalidates the query so promotions/demotions propagate. Mounted ONCE at the
// route (drives promotion detection AND feeds the presentational GuestQueueBar),
// so there is a single channel per room.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { supabase } from '@/lib/supabase';

export interface RoomQueue {
  gridSlots: number;
  onGridCount: number;
  yourStatus: 'on_grid' | 'waiting' | 'none';
  yourRank: number | null;
  waitingCount: number;
  /** Set only when the caller is on-grid AND in their outbid grace window (Inc 4b). */
  yourGraceUntil: string | null;
}

async function getRoomQueue(roomId: string): Promise<RoomQueue> {
  const { data, error } = await supabase.rpc('get_room_queue', { p_room_id: roomId });
  if (error) throw error;
  const r = data?.[0];
  if (!r) throw new Error('get_room_queue returned no row');
  return {
    gridSlots: r.grid_slots,
    onGridCount: r.on_grid_count,
    yourStatus: r.your_status as RoomQueue['yourStatus'],
    yourRank: r.your_rank,
    waitingCount: r.waiting_count,
    yourGraceUntil: r.your_grace_until ?? null,
  };
}

// roomId = rooms.id (uuid) — matches room_grid_slots.room_id.
export function useGuestQueue(roomId: string) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['room-queue', roomId],
    enabled: Boolean(roomId),
    queryFn: () => getRoomQueue(roomId),
  });
  useEffect(() => {
    if (!roomId) return;
    const channel = supabase
      .channel(`room-queue:${roomId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'room_grid_slots', filter: `room_id=eq.${roomId}` },
        () => void qc.invalidateQueries({ queryKey: ['room-queue', roomId] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [roomId, qc]);
  return query;
}
